/**
 * Les notifications poussées, côté navigateur.
 *
 * Trois acteurs, et il faut les trois : l'autorisation du système, un abonnement auprès du
 * fournisseur de poussée du navigateur, et notre serveur à qui l'on confie cet abonnement. Si
 * l'un manque, rien n'arrive — et chacun peut être révoqué sans prévenir, d'où l'état qu'on
 * relit à chaque ouverture de l'écran plutôt que de le mémoriser.
 */

/** Ce que l'appareil permet aujourd'hui. */
export type EtatDesNotifications =
  /** Le navigateur ne sait pas faire : ancien, ou mode privé. */
  | 'indisponible'
  /** Possible, mais pas encore demandé — ou demandé et refusé. */
  | 'inactif'
  | 'refuse'
  | 'actif'

/**
 * Convertit la clé publique du serveur en octets.
 *
 * `applicationServerKey` n'accepte pas la chaîne base64url que le serveur rend : il lui faut les
 * octets. La conversion est ici, isolée et éprouvée, parce qu'une erreur d'un octet ne lève pas
 * — elle produit un abonnement que le fournisseur refusera plus tard, loin de la cause.
 */
export function cleBinaire(base64url: string): Uint8Array<ArrayBuffer> {
  const base64 = base64url.replace(/-/gu, '+').replace(/_/gu, '/')
  const complement = '='.repeat((4 - (base64.length % 4)) % 4)
  const brut = atob(base64 + complement)

  // Le tampon est construit explicitement plutôt que par `Uint8Array.from` : ce dernier rend un
  // `Uint8Array<ArrayBufferLike>`, que `applicationServerKey` refuse — il exige un tampon dont
  // on sait qu'il n'est pas partagé entre fils d'exécution.
  const octets = new Uint8Array(new ArrayBuffer(brut.length))

  for (let rang = 0; rang < brut.length; rang += 1) {
    octets[rang] = brut.charCodeAt(rang)
  }

  return octets
}

/** Les octets d'une clé d'abonnement, en base64url — la forme attendue par le serveur. */
export function enBase64url(donnees: ArrayBuffer | null): string {
  if (!donnees) {
    return ''
  }

  let binaire = ''

  for (const octet of new Uint8Array(donnees)) {
    binaire += String.fromCharCode(octet)
  }

  return btoa(binaire).replace(/\+/gu, '-').replace(/\//gu, '_').replace(/=+$/u, '')
}

export function supporteLesNotifications(): boolean {
  return (
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  )
}

/** L'état courant, relu à chaque fois plutôt que mémorisé. */
export async function etatDesNotifications(): Promise<EtatDesNotifications> {
  if (!supporteLesNotifications()) {
    return 'indisponible'
  }

  if (Notification.permission === 'denied') {
    return 'refuse'
  }

  const enregistrement = await navigator.serviceWorker.ready
  const abonnement = await enregistrement.pushManager.getSubscription()

  return abonnement === null ? 'inactif' : 'actif'
}

/** L'abonnement de cet appareil, prêt à être confié au serveur. */
export type AbonnementLocal = {
  readonly endpoint: string
  readonly p256dh: string
  readonly auth: string
  readonly offsetMinutes: number
}

/**
 * Demande l'autorisation puis s'abonne. Rend `null` si l'autorisation est refusée.
 *
 * L'autorisation est demandée <b>au moment du geste</b> et non à l'ouverture de l'application :
 * une demande qui tombe sans qu'on ait rien demandé se refuse par réflexe, et un refus ne se
 * reprend qu'en allant dans les réglages du navigateur.
 */
export async function activerLesNotifications(clePublique: string): Promise<AbonnementLocal | null> {
  if (!supporteLesNotifications()) {
    return null
  }

  if ((await Notification.requestPermission()) !== 'granted') {
    return null
  }

  const enregistrement = await navigator.serviceWorker.ready

  const abonnement =
    (await enregistrement.pushManager.getSubscription()) ??
    (await enregistrement.pushManager.subscribe({
      // Exigé par Chrome : toute poussée doit se traduire par une notification visible. C'est
      // ce qui interdit de s'en servir pour suivre quelqu'un en silence.
      userVisibleOnly: true,
      applicationServerKey: cleBinaire(clePublique),
    }))

  return {
    endpoint: abonnement.endpoint,
    p256dh: enBase64url(abonnement.getKey('p256dh')),
    auth: enBase64url(abonnement.getKey('auth')),
    offsetMinutes: new Date().getTimezoneOffset(),
  }
}

/** Coupe les notifications sur cet appareil. Rend l'adresse retirée, pour le dire au serveur. */
export async function desactiverLesNotifications(): Promise<string | null> {
  if (!supporteLesNotifications()) {
    return null
  }

  const enregistrement = await navigator.serviceWorker.ready
  const abonnement = await enregistrement.pushManager.getSubscription()

  if (abonnement === null) {
    return null
  }

  const adresse = abonnement.endpoint
  await abonnement.unsubscribe()

  return adresse
}
